import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import PreviewModal from "@/components/PreviewModal";
import DeleteShipmentModal from "@/components/DeleteShipmentModal";
import api, { API, formatApiError, DOC_TYPE_LABELS, DOC_TYPE_COLORS, REQUIRED_DOC_TYPES, withAutoRetry } from "@/lib/api";
import { isFolderSupported, saveBlobToFolder, getFolderName, markDocSaved, getSavedIds, getAutoSave, recordHiccupRecovery } from "@/lib/localFolder";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Airplane, CheckCircle, WarningCircle, FileArrowUp, FileArrowDown,
  TrashSimple, Plus, ShareNetwork, Copy, Eye, FloppyDisk, X, UploadSimple,
  DownloadSimple, PencilSimple,
} from "@phosphor-icons/react";

const OPTIONAL_TYPE = "other";

// Auto-detect doc type from filename
export function detectDocType(filename) {
  const n = (filename || "").toLowerCase();
  if (/awb|air.?way|way.?bill/.test(n)) return "awb_form";
  if (/(^|[^a-z])inv(oice)?([^a-z]|$)/.test(n)) return "invoice";
  if (/permit/.test(n)) return "permit";
  if (/terminal|term(_|-|\s)|charges/.test(n)) return "terminal_charges";
  if (/customs?|declaration/.test(n)) return "customs_form";
  return "other";
}

export default function ShipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ship, setShip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [folderName, setFolderName] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [saveAll, setSaveAll] = useState(null); // null | {done, total, current, errors, skipped}
  const [savedMap, setSavedMap] = useState({}); // {docId: true} — locally-saved status
  const [showEdit, setShowEdit] = useState(false);
  const autoSaveRef = useRef(false);
  const autoSavingRef = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/shipments/${id}`);
      setShip(data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/shipments");
    } finally { setLoading(false); }
  };

  const [workersCanEdit, setWorkersCanEdit] = useState(true);
  useEffect(() => { api.get("/app-settings").then(({ data }) => setWorkersCanEdit(data.shipments_editable_by_workers !== false)).catch(() => {}); }, []);

  useEffect(() => { load(); getFolderName().then(setFolderName); getAutoSave().then((v) => { autoSaveRef.current = v; }); /* eslint-disable-next-line */ }, [id]);

  // Refresh saved-locally map when documents list changes
  useEffect(() => {
    if (!ship?.documents) return;
    getSavedIds(ship.documents.map((d) => d.id)).then(setSavedMap);
  }, [ship?.documents]);

  // Auto-save when shipment becomes complete (declared BEFORE conditional return per rules-of-hooks)
  useEffect(() => {
    if (!ship || !ship.is_complete) return;
    if (!autoSaveRef.current) return;
    if (autoSavingRef.current) return;
    if (!isFolderSupported() || !folderName) return;
    const pending = ship.documents.filter((d) => !savedMap[d.id]);
    if (pending.length === 0) return;
    autoSavingRef.current = true;
    runSaveAll({ silent: true }).finally(() => { autoSavingRef.current = false; });
    // eslint-disable-next-line
  }, [ship?.is_complete, ship?.documents, folderName, savedMap]);

  if (loading || !ship) {
    return <Layout><div className="text-sm text-zinc-500 font-mono">Loading shipment…</div></Layout>;
  }

  const uploaded = ship.documents.reduce((m, d) => {
    (m[d.doc_type] = m[d.doc_type] || []).push(d);
    return m;
  }, {});

  const delDoc = async (d) => {
    if (!window.confirm(`Delete ${DOC_TYPE_LABELS[d.doc_type]}?`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Document removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delShipment = () => setShowDelete(true);

  const saveLocal = async (d) => {
    try {
      const { data } = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const saved = await saveBlobToFolder(d.original_filename, data, ship.awb_number);
      await markDocSaved(d.id, { filename: saved, awb: ship.awb_number });
      setSavedMap((m) => ({ ...m, [d.id]: true }));
      toast.success(`Saved as ${saved} to ${folderName}\\${ship.awb_number}\\`);
    } catch (e) { toast.error(e.message || formatApiError(e)); }
  };

  const runSaveAll = async ({ silent } = {}) => {
    if (!isFolderSupported()) {
      if (!silent) toast.error("Your browser doesn't support saving to a local folder. Use Chrome or Edge.");
      return;
    }
    if (!folderName) {
      if (!silent) {
        toast.error("Pick a folder first in Settings → Save to local folder");
        navigate("/settings");
      }
      return;
    }
    // Filter out docs already saved locally
    const pending = ship.documents.filter((d) => !savedMap[d.id]);
    if (pending.length === 0) {
      if (!silent) toast.info("All documents are already saved locally");
      return;
    }
    const total = pending.length;
    const skipped = ship.documents.length - total;
    if (!silent) setSaveAll({ done: 0, total, current: "", errors: [], skipped });
    let done = 0;
    const errors = [];
    for (const d of pending) {
      if (!silent) setSaveAll({ done, total, current: d.original_filename, errors, skipped, retrying: 0 });
      try {
        const { data } = await withAutoRetry(
          () => api.get(`/documents/${d.id}/download`, { responseType: "blob" }),
          {
            onRetry: ({ attempt }) => !silent && setSaveAll((s) => s && ({ ...s, retrying: attempt })),
            onRecovered: recordHiccupRecovery,
          },
        );
        await saveBlobToFolder(d.original_filename, data, ship.awb_number);
        await markDocSaved(d.id, { awb: ship.awb_number });
        setSavedMap((m) => ({ ...m, [d.id]: true }));
        done += 1;
      } catch (e) {
        errors.push({ file: d.original_filename, error: e.message || formatApiError(e) });
      }
    }
    if (!silent) setSaveAll({ done, total, current: "", errors, skipped });
    if (silent) {
      if (done > 0) toast.success(`Auto-saved ${done} new file${done === 1 ? "" : "s"} to ${folderName}\\${ship.awb_number}\\`);
      return;
    }
    if (errors.length === 0) {
      const msg = skipped > 0
        ? `Saved ${done} new file${done === 1 ? "" : "s"} (${skipped} already saved) to ${folderName}\\${ship.awb_number}\\`
        : `Saved ${done} files to ${folderName}\\${ship.awb_number}\\`;
      toast.success(msg);
      setTimeout(() => navigate("/shipments"), 700);
    } else {
      toast.error(`Saved ${done} of ${total}. ${errors.length} failed.`);
    }
  };

  const saveAllAndFinish = () => runSaveAll({ silent: false });

  const canEdit = user?.role === "admin" || ship.created_by_id === user?.id;

  return (
    <Layout>
      <button onClick={() => navigate("/shipments")} data-testid="back-btn"
        className="text-xs text-zinc-500 font-mono flex items-center gap-1 hover:text-zinc-900 transition-colors duration-150 mb-4">
        <ArrowLeft size={12} /> BACK TO SHIPMENTS
      </button>

      {/* Header */}
      <div className="bg-white border border-zinc-200 p-6 mb-4">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">Shipment · Drive {ship.drive}:</div>
            <div className="flex items-center gap-3 flex-wrap">
              <Airplane size={30} weight="duotone" className="text-[#FF4F00]" />
              <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-zinc-950 font-mono" data-testid="ship-awb">
                {ship.awb_number}
              </h1>
              {ship.is_complete ? (
                <span className="inline-flex items-center gap-1 text-xs font-mono border border-emerald-200 bg-emerald-50 text-emerald-800 px-2.5 py-1" data-testid="ship-status-complete">
                  <CheckCircle size={14} weight="fill" /> COMPLETE
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-mono border border-orange-200 bg-orange-50 text-[#FF4F00] px-2.5 py-1" data-testid="ship-status-incomplete">
                  <WarningCircle size={14} weight="fill" /> {ship.docs_missing.length} MISSING
                </span>
              )}
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
              <Meta label="Date" value={ship.doc_date || "—"} mono />
              <Meta label="Consignee" value={ship.consignee || "—"} />
              <Meta label="Customs 1" value={ship.customs_no_1 || "—"} mono />
              <Meta label="Customs 2" value={ship.customs_no_2 || "—"} mono />
            </div>
            {ship.notes && <div className="mt-3 text-sm text-zinc-600 italic border-l-2 border-zinc-200 pl-3">{ship.notes}</div>}
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className="text-[10px] font-mono text-zinc-400">Created by {ship.created_by_name}</div>
            {ship.documents.length > 0 && isFolderSupported() && (
              <button onClick={saveAllAndFinish} data-testid="save-all-btn"
                className="bg-emerald-600 text-white text-xs px-3 py-2 hover:bg-emerald-700 flex items-center gap-1.5 transition-colors duration-150 border border-emerald-700"
                title={folderName ? `Save all ${ship.documents.length} files to ${folderName}\\${ship.awb_number}\\` : "Pick a folder in Settings first"}>
                <DownloadSimple size={14} weight="bold" />
                Save all to folder & finish
              </button>
            )}
            {(user?.role === "admin" || workersCanEdit) && (
              <button onClick={() => setShowEdit(true)} data-testid="edit-shipment-btn"
                className="text-xs text-[#002FA7] flex items-center gap-1 hover:bg-blue-50 px-2 py-1 transition-colors duration-150 border border-blue-200">
                <PencilSimple size={12} /> Edit shipment
              </button>
            )}
            {canEdit && (
              <button onClick={delShipment} data-testid="del-shipment-btn"
                className="text-xs text-red-700 flex items-center gap-1 hover:bg-red-50 px-2 py-1 transition-colors duration-150 border border-red-200">
                <TrashSimple size={12} /> Delete shipment
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk upload + share row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <BulkUpload shipmentId={id} onDone={load} />
        <SharePanel shipment={ship} canEdit={canEdit} onChange={load} />
      </div>

      {/* Required doc slots */}
      <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Required Documents</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {REQUIRED_DOC_TYPES.map((t) => (
          <DocSlot key={t} docType={t} shipmentId={id} docs={uploaded[t] || []}
            onChange={load} onPreview={setPreview} onDelete={delDoc} onSaveLocal={saveLocal}
            user={user} folderName={folderName} savedMap={savedMap} required />
        ))}
      </div>

      {/* Others */}
      <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Others (optional)</div>
      <DocSlot docType={OPTIONAL_TYPE} shipmentId={id} docs={uploaded[OPTIONAL_TYPE] || []}
        onChange={load} onPreview={setPreview} onDelete={delDoc} onSaveLocal={saveLocal}
        user={user} folderName={folderName} savedMap={savedMap} />

      {preview && <PreviewModal doc={preview} onClose={() => setPreview(null)} subfolder={ship.awb_number} />}
      {showDelete && (
        <DeleteShipmentModal
          shipment={ship}
          onClose={() => setShowDelete(false)}
          onDeleted={() => navigate("/shipments")}
        />
      )}

      {showEdit && (
        <EditShipmentModal
          shipment={ship}
          onClose={() => setShowEdit(false)}
          onSaved={(updated) => { setShip((prev) => ({ ...prev, ...updated })); setShowEdit(false); }}
        />
      )}

      {saveAll && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="save-all-progress">
          <div className="bg-white border border-zinc-200 shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 bg-emerald-600 text-white flex items-center justify-center">
                <DownloadSimple size={18} weight="bold" />
              </div>
              <div>
                <div className="font-display font-black text-lg text-zinc-950 tracking-tight leading-tight">Saving to local folder</div>
                <div className="text-xs text-zinc-500 font-mono">{folderName}\{ship.awb_number}\</div>
              </div>
            </div>
            <div className="text-sm text-zinc-700 mb-2">
              {saveAll.done} of {saveAll.total} files saved
            </div>
            <div className="w-full h-2 bg-zinc-100 border border-zinc-200 mb-3">
              <div className="h-full bg-emerald-500 transition-all duration-200"
                style={{ width: `${(saveAll.done / saveAll.total) * 100}%` }} />
            </div>
            {saveAll.current && (
              <div className="text-xs font-mono text-zinc-500 truncate">
                → {saveAll.current}
                {saveAll.retrying > 0 && (
                  <span className="ml-2 text-amber-600" data-testid="retry-indicator">
                    (retry {saveAll.retrying})
                  </span>
                )}
              </div>
            )}
            {saveAll.errors.length > 0 && (
              <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 max-h-32 overflow-y-auto">
                {saveAll.errors.map((e, i) => (
                  <div key={i}>• {e.file}: {e.error}</div>
                ))}
              </div>
            )}
            {saveAll.done + saveAll.errors.length >= saveAll.total && (
              <div className="mt-4 flex justify-end">
                <button onClick={() => setSaveAll(null)}
                  className="px-3 py-2 text-sm border border-zinc-200 bg-white hover:bg-zinc-100 transition-colors duration-150"
                  data-testid="save-all-close-btn">
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{label}</div>
      <div className={`text-sm text-zinc-900 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function BulkUpload({ shipmentId, onDone }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState([]); // [{name, type, status}]

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    const list = Array.from(files).map((f) => ({
      name: f.name, type: detectDocType(f.name), status: "pending", file: f,
    }));
    setProgress(list);
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      try {
        const fd = new FormData();
        fd.append("file", item.file);
        fd.append("shipment_id", shipmentId);
        fd.append("doc_type", item.type);
        await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        list[i] = { ...item, status: "done" };
      } catch (e) {
        list[i] = { ...item, status: "error", error: formatApiError(e) };
      }
      setProgress([...list]);
    }
    setBusy(false);
    const okCount = list.filter((x) => x.status === "done").length;
    if (okCount > 0) toast.success(`${okCount} of ${list.length} files uploaded`);
    if (okCount < list.length) toast.error(`${list.length - okCount} failed`);
    onDone();
    setTimeout(() => setProgress([]), 4000);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) upload(e.dataTransfer.files);
  };

  return (
    <div className="lg:col-span-2 border border-zinc-200 bg-white p-4" data-testid="bulk-upload">
      <div className="flex items-center gap-2 mb-3">
        <UploadSimple size={16} className="text-[#002FA7]" />
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
          Bulk upload — drop multiple files, doc type auto-detected from filename
        </div>
      </div>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => !busy && ref.current?.click()}
        className={`border-2 border-dashed p-5 text-center cursor-pointer transition-colors duration-150 ${
          busy ? "border-zinc-200 bg-zinc-50 cursor-wait" : "border-zinc-300 bg-zinc-50 hover:bg-zinc-100 hover:border-[#002FA7]"
        }`}
        data-testid="bulk-dropzone"
      >
        <FileArrowUp size={28} weight="duotone" className="text-[#002FA7] mx-auto mb-2" />
        <div className="text-sm text-zinc-800 font-medium">
          {busy ? "Uploading…" : "Drop files here or click to select"}
        </div>
        <div className="text-xs text-zinc-500 font-mono mt-1">
          Name hints: <b>AWB</b>, <b>invoice</b>, <b>permit</b>, <b>terminal</b>, <b>customs</b>
        </div>
        <input ref={ref} type="file" multiple hidden
          onChange={(e) => upload(e.target.files)} data-testid="bulk-file-input" />
      </div>

      {progress.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="bulk-progress">
          {progress.map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className={`inline-block px-2 py-0.5 font-mono border ${DOC_TYPE_COLORS[p.type]}`}>
                {DOC_TYPE_LABELS[p.type]}
              </span>
              <span className="truncate flex-1 text-zinc-700">{p.name}</span>
              {p.status === "pending" && <span className="text-zinc-400 font-mono">…</span>}
              {p.status === "done" && <CheckCircle size={14} weight="fill" className="text-emerald-600" />}
              {p.status === "error" && <span className="text-red-600" title={p.error}>✕</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SharePanel({ shipment, canEdit, onChange }) {
  const [busy, setBusy] = useState(false);
  const token = shipment.share_token || null;
  const link = token ? `${window.location.origin}/public/shipments/${token}` : null;

  const enable = async () => {
    setBusy(true);
    try {
      await api.post(`/shipments/${shipment.id}/share`);
      toast.success("Public link enabled");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    if (!window.confirm("Revoke the public share link?")) return;
    setBusy(true);
    try {
      await api.delete(`/shipments/${shipment.id}/share`);
      toast.success("Public link revoked");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    if (!link) return;
    try { await navigator.clipboard.writeText(link); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="border border-zinc-200 bg-white p-4" data-testid="share-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShareNetwork size={16} className="text-[#002FA7]" />
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Consignee share link</div>
      </div>
      {token ? (
        <>
          <div className="flex items-center gap-1 mb-2">
            <input readOnly value={link || ""} data-testid="share-link-input"
              className="flex-1 text-xs border border-zinc-200 px-2 py-1.5 font-mono bg-zinc-50" />
            <button onClick={copy} data-testid="share-copy-btn" aria-label="Copy"
              className="text-xs border border-zinc-200 bg-white p-1.5 hover:bg-zinc-100">
              <Copy size={12} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <a href={link} target="_blank" rel="noopener noreferrer" data-testid="share-open-btn"
              className="text-xs text-[#002FA7] hover:underline">Preview →</a>
            {canEdit && (
              <button onClick={disable} disabled={busy} data-testid="share-revoke-btn"
                className="ml-auto text-xs text-red-700 hover:bg-red-50 px-2 py-1 flex items-center gap-1">
                <X size={12} /> Revoke
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="text-xs text-zinc-500 mb-3">
            Generate a read-only link to share with your customer. Revoke anytime.
          </div>
          {canEdit ? (
            <button onClick={enable} disabled={busy} data-testid="share-enable-btn"
              className="bg-[#002FA7] text-white text-xs px-3 py-2 hover:bg-[#00227A] disabled:opacity-60 flex items-center gap-1 transition-colors duration-150">
              <ShareNetwork size={12} /> {busy ? "Creating…" : "Enable link"}
            </button>
          ) : (
            <div className="text-xs text-zinc-400 italic">Only the shipment creator or admin can enable sharing.</div>
          )}
        </>
      )}
    </div>
  );
}

function DocSlot({ docType, shipmentId, docs, onChange, onPreview, onDelete, onSaveLocal, user, folderName, savedMap, required }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const hasFile = docs.length > 0;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("shipment_id", shipmentId);
      fd.append("doc_type", docType);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${DOC_TYPE_LABELS[docType]} uploaded`);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) upload(e.dataTransfer.files[0]);
  };

  const stateClass = required && !hasFile
    ? "border-orange-300 bg-orange-50/50"
    : hasFile ? "border-emerald-200 bg-white" : "border-zinc-200 bg-white";

  return (
    <div className={`border p-4 ${stateClass}`} data-testid={`slot-${docType}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`inline-block px-2 py-0.5 text-[10px] font-mono border ${DOC_TYPE_COLORS[docType]}`}>
            {DOC_TYPE_LABELS[docType]}
          </span>
          {required && !hasFile && (
            <span className="text-[10px] font-mono text-[#FF4F00] uppercase tracking-widest">Missing</span>
          )}
          {hasFile && <CheckCircle size={16} weight="fill" className="text-emerald-600" />}
        </div>
      </div>

      {docs.length === 0 ? (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => ref.current?.click()}
          className="border-2 border-dashed border-zinc-300 bg-white hover:bg-zinc-50 hover:border-[#002FA7] cursor-pointer p-5 text-center transition-colors duration-150"
          data-testid={`dropzone-${docType}`}
        >
          <FileArrowUp size={24} weight="duotone" className="text-[#002FA7] mx-auto mb-1" />
          <div className="text-xs font-mono text-zinc-600">{busy ? "Uploading…" : "Click or drop file"}</div>
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between text-sm border border-zinc-200 bg-white px-3 py-2" data-testid={`doc-${d.id}`}>
              <div className="truncate flex-1 pr-2">
                <div className="text-zinc-900 truncate">{d.original_filename}</div>
                <div className="text-[10px] font-mono text-zinc-400 flex items-center gap-2">
                  <span>{(d.size / 1024).toFixed(1)} KB · {d.uploaded_by_name}</span>
                  {savedMap?.[d.id] && (
                    <span className="inline-flex items-center gap-0.5 text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[9px] uppercase tracking-wider" data-testid={`saved-local-${d.id}`}>
                      <CheckCircle size={9} weight="fill" /> Saved locally
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => onPreview(d)} data-testid={`preview-${d.id}`} aria-label="Preview"
                  className="text-zinc-700 p-1 hover:bg-zinc-100 transition-colors duration-150" title="Preview">
                  <Eye size={14} />
                </button>
                {isFolderSupported() && folderName && (
                  <button onClick={() => onSaveLocal(d)} data-testid={`save-local-${d.id}`} aria-label="Save to folder"
                    className="text-zinc-700 p-1 hover:bg-zinc-100 transition-colors duration-150" title={`Save to ${folderName}`}>
                    <FloppyDisk size={14} />
                  </button>
                )}
                <button onClick={() => window.open(`${API}/documents/${d.id}/download?auth=${encodeURIComponent(localStorage.getItem('access_token') || '')}`, '_blank')}
                  data-testid={`download-${d.id}`} aria-label="Download"
                  className="text-[#002FA7] p-1 hover:bg-blue-50 transition-colors duration-150" title="Download">
                  <FileArrowDown size={14} />
                </button>
                {(user?.role === "admin" || d.uploaded_by_id === user?.id) && (
                  <button onClick={() => onDelete(d)} data-testid={`delete-${d.id}`} aria-label="Delete"
                    className="text-red-700 p-1 hover:bg-red-50 transition-colors duration-150" title="Delete">
                    <TrashSimple size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {docType === OPTIONAL_TYPE ? (
            <button type="button" onClick={() => ref.current?.click()}
              className="text-xs text-[#002FA7] flex items-center gap-1 hover:underline"
              data-testid={`add-more-${docType}`}>
              <Plus size={12} /> Add another
            </button>
          ) : (
            <button type="button" onClick={() => ref.current?.click()}
              className="text-xs text-zinc-500 hover:text-zinc-900"
              data-testid={`replace-${docType}`}>
              Replace file
            </button>
          )}
        </div>
      )}
      <input ref={ref} type="file" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid={`file-input-${docType}`} />
    </div>
  );
}

function EditShipmentModal({ shipment, onClose, onSaved }) {
  const [awb, setAwb] = useState(shipment.awb_number || "");
  const [date, setDate] = useState(shipment.doc_date || "");
  const [consignee, setConsignee] = useState(shipment.consignee || "");
  const [c1, setC1] = useState(shipment.customs_no_1 || "");
  const [c2, setC2] = useState(shipment.customs_no_2 || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!awb.trim()) { toast.error("AWB number is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/shipments/${shipment.id}`, {
        awb_number: awb.trim(),
        doc_date: date.trim(),
        consignee: consignee.trim(),
        customs_no_1: c1.trim(),
        customs_no_2: c2.trim(),
        notes: shipment.notes || "",
        drive: shipment.drive || "C",
      });
      toast.success("Shipment updated");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-shipment-modal">
      <form onSubmit={submit} className="bg-white border border-zinc-200 shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <PencilSimple size={16} className="text-[#002FA7]" />
            <div className="font-display font-black text-lg tracking-tight text-zinc-950">Edit Shipment</div>
          </div>
          <button type="button" onClick={onClose} data-testid="edit-close-btn" className="text-zinc-500 hover:text-zinc-900 p-1">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <EditField label="AWB Number" required>
            <input value={awb} onChange={(e) => setAwb(e.target.value)} data-testid="edit-awb-input"
              className="w-full font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
          </EditField>
          <EditField label="Date">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="edit-date-input"
              className="w-full font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
          </EditField>
          <EditField label="Customer (Consignee)">
            <input value={consignee} onChange={(e) => setConsignee(e.target.value)} data-testid="edit-consignee-input"
              className="w-full text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
          </EditField>
          <div className="grid grid-cols-2 gap-3">
            <EditField label="Customs 1">
              <input value={c1} onChange={(e) => setC1(e.target.value)} data-testid="edit-c1-input"
                className="w-full font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
            </EditField>
            <EditField label="Customs 2">
              <input value={c2} onChange={(e) => setC2(e.target.value)} data-testid="edit-c2-input"
                className="w-full font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
            </EditField>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-5 py-3 bg-zinc-50">
          <button type="button" onClick={onClose} data-testid="edit-cancel-btn"
            className="text-sm border border-zinc-200 bg-white px-3 py-2 hover:bg-zinc-100 transition-colors duration-150">
            Cancel
          </button>
          <button type="submit" disabled={saving} data-testid="edit-save-btn"
            className="text-sm bg-[#002FA7] text-white px-4 py-2 hover:bg-[#00227A] disabled:opacity-50 transition-colors duration-150">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditField({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">
        {label}{required && <span className="text-[#FF4F00]"> *</span>}
      </div>
      {children}
    </label>
  );
}
